<template>
  <div style="background-color: #212121; min-height: 100vh" class="pa-md-8 pa-4 white--text">
    <v-container fluid>
      <div v-if="!finishedLoading" class="text-center py-16">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-h6 grey--text">Statistiken werden geladen...</p>
      </div>

      <div v-if="finishedLoading && statistics">
        <h1 class="text-h3 font-weight-bold mb-2">Monatsziele Analyse</h1>
        <v-switch v-model="anonymizeStudios" label="Studio-Namen anonymisieren" dark color="primary"></v-switch>

        <v-divider class="my-8"></v-divider>

        <div class="mb-12">
          <h2 class="text-h4 font-weight-bold mb-6 d-flex align-center">
            <v-icon large left color="primary">mdi-chart-bar</v-icon>
            Zielerreichung (Geplant vs. IST)
          </h2>
          <v-row>
            <v-col cols="12">
              <v-card dark color="rgba(45, 45, 45, 0.6)" class="card-raised">
                <v-card-title class="pb-0">
                  <span class="font-weight-bold">Leistung pro Studio (Sortiert)</span>
                  <v-spacer></v-spacer>
                  <v-select
                    v-model="selectedBarChartMetric"
                    :items="barChartMetricOptions"
                    item-text="label"
                    item-value="key"
                    label="Metrik auswählen"
                    dense
                    outlined
                    hide-details
                    style="max-width: 250px"></v-select>
                </v-card-title>
                <apexchart type="bar" height="400" :options="studioBarChartOptions" :series="studioBarChartSeries"></apexchart>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" v-for="(metric, key) in statistics.totals" :key="key">
              <v-card dark color="rgba(45, 45, 45, 0.6)" class="card-raised">
                <v-card-title class="text-subtitle-1">{{ metric.label }} - Gesamt</v-card-title>
                <apexchart type="bar" height="150" :options="totalBarChartOptions(metric)" :series="totalBarChartSeries(metric)"></apexchart>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-8"></v-divider>

        <div>
          <h2 class="text-h4 font-weight-bold mb-6 d-flex align-center">
            <v-icon large left color="primary">mdi-spider-web</v-icon>
            Stärken- & Schwächen-Analyse
          </h2>
          <v-row>
            <v-col cols="12" xl="6" lg="6">
              <v-card dark color="rgba(45, 45, 45, 0.6)" class="card-raised fill-height">
                <v-card-title>
                  Gesamt-Durchschnitt
                  <v-spacer></v-spacer>
                  <v-btn small color="primary" @click="openEmployeeDialog('total')">Pro Mitarbeiter</v-btn>
                </v-card-title>
                <v-card-text>
                  <apexchart type="radar" height="400" :options="radarChartOptions('totalRadar')" :series="totalRadarSeries"></apexchart>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" xl="6" lg="6" v-for="(studio, index) in studioRadarSeries" :key="studio.name">
              <lazy-render :placeholder-height="460">
                <v-card dark color="rgba(45, 45, 45, 0.6)" class="card-raised fill-height">
                  <v-card-title>
                    {{ studio.name }}
                    <v-spacer></v-spacer>
                    <v-btn small color="primary" @click="openEmployeeDialog(studio)">Pro Mitarbeiter</v-btn>
                  </v-card-title>
                  <v-card-text>
                    <apexchart type="radar" height="400" :options="radarChartOptions('studioRadar' + index)" :series="studio.series"></apexchart>
                  </v-card-text>
                </v-card>
              </lazy-render>
            </v-col>
          </v-row>
        </div>
      </div>

      <v-dialog v-model="dialogVisible" fullscreen hide-overlay transition="dialog-bottom-transition" scrollable>
        <v-card style="background-color: #212121" class="white--text">
          <v-toolbar dark color="primary">
            <v-btn icon dark @click="dialogVisible = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title>Mitarbeiter-Detailansicht: {{ selectedDialogGroup ? selectedDialogGroup.name : "Gesamt" }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="dialogView" mandatory dense borderless background-color="transparent">
              <v-btn value="chart" icon><v-icon>mdi-chart-spider</v-icon></v-btn>
              <v-btn value="table" icon><v-icon>mdi-table</v-icon></v-btn>
            </v-btn-toggle>
            <v-select
              v-model="employeeSortKey"
              :items="
                barChartMetricOptions.map((m) => {
                  if (m.label === 'packageCount') m.label = 'Pakete'
                  if (m.label === 'medicalHistory') m.label = 'Anamnesebögen'

                  return { key: m.key, label: m.label }
                })
              "
              item-text="label"
              item-value="key"
              label="Sortieren nach"
              dense
              hide-details
              outlined
              style="max-width: 220px; margin-right: 16px"></v-select>
            <v-btn icon @click="employeeSortDesc = !employeeSortDesc">
              <v-icon>{{ employeeSortDesc ? "mdi-sort-descending" : "mdi-sort-ascending" }}</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pa-md-8 pa-4">
            <v-row v-if="employeesForDialog.length > 0">
              <v-col v-for="employee in employeesForDialog" :key="employee._id" cols="12" md="6" lg="6" xl="4">
                <lazy-render :placeholder-height="550">
                  <v-card dark color="rgba(45, 45, 45, 0.6)" class="card-raised fill-height">
                    <v-card-title>{{ employee.fullName }}</v-card-title>
                    <v-card-text>
                      <apexchart
                        v-if="dialogView === 'chart'"
                        type="radar"
                        height="450"
                        :options="radarChartOptions('employeeRadar', employee._id)"
                        :series="employeeRadarSeries(employee)"></apexchart>

                      <v-simple-table v-else dense dark>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">Metrik</th>
                              <th class="text-right">Geplant</th>
                              <th class="text-right">Haben</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="item in getEmployeeTableData(employee)" :key="item.label">
                              <td class="text-left d-flex align-center">
                                <div
                                  :style="{
                                    backgroundColor: item.color,
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    marginRight: '8px',
                                  }"></div>
                                {{ item.label }}
                              </td>
                              <td class="text-right">{{ item.planned }}</td>
                              <td class="text-right">{{ item.actual }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-card-text>
                  </v-card>
                </lazy-render>
              </v-col>
            </v-row>
            <div v-else class="text-center py-16">
              <p class="text-h6 grey--text">Keine Mitarbeiterdaten für diese Auswahl gefunden.</p>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import api from "@/api"
import VueApexCharts from "vue-apexcharts"
import LazyRender from "./homeDashboardGoalsStatisticsLazyRender.vue"

const METRICS_CONFIG = {
  contracts: { label: "Verträge", color: "#1E88E5" },
  packages: { label: "Pakete", color: "#00ACC1" }, // Changed from packageCount
  anamneses: { label: "Anamnesebögen", color: "#43A047" }, // Changed from medicalHistory
  workouts: { label: "Trainingspläne", color: "#FDD835" },
  workoutsPerCustomer: { label: "Versch. Kunden TP", color: "#FB8C00" },
  interactions: { label: "Kundeninteraktionen", color: "#E53935" },
  upgrades: { label: "Upgrades", color: "#8E24AA" },
  appointments: { label: "Termine", color: "#D81B60" },
}
const METRIC_KEYS = Object.keys(METRICS_CONFIG)
const METRIC_COLORS = Object.values(METRICS_CONFIG).map((m) => m.color)

export default {
  name: "TrainerStatistics",
  components: { apexchart: VueApexCharts, LazyRender },
  props: {
    studios: { type: Array, required: true },
    datePickerRange: { type: Array, required: true },
  },
  data() {
    return {
      statistics: null,
      allEmployees: [],
      finishedLoading: false,
      anonymizeStudios: true,
      selectedBarChartMetric: "contracts",
      dialogVisible: false,
      selectedDialogGroup: null,
      dialogView: "chart",
      employeeSortKey: "contracts", // Standard-Sortierung nach Verträgen
      employeeSortDesc: true, // true = absteigend, false = aufsteigend
    }
  },
  async created() {
    this.loadStatistics()
  },
  watch: {
    studios: "loadStatistics",
    datePickerRange: "loadStatistics",
  },
  methods: {
    getEmployeeTableData(employee) {
      // Diese Funktion erstellt die Zeilen für die Tabelle eines Mitarbeiters
      return METRIC_KEYS.map((key) => {
        const goalValue = employee.trainerGoal?.goals?.[key] || 0
        let actualValue = 0

        // Hier mappen wir den Key auf den korrekten "Haben"-Wert im Mitarbeiter-Objekt
        switch (key) {
          case "contracts":
            actualValue = employee.contracts
            break
          case "packages":
            actualValue = employee.packageCount
            break
          case "anamneses":
            actualValue = employee.medicalHistory
            break
          case "workouts":
            actualValue = employee.workouts
            break
          case "workoutsPerCustomer":
            actualValue = employee.workoutsPerCustomer
            break
          case "interactions":
            actualValue = employee.interactions?.totalInteractions || 0
            break
          case "upgrades":
            actualValue = employee.upgrades
            break
          case "appointments":
            actualValue = employee.trainerGoal?.goals?.appointmentsActual || 0
            break
        }

        return {
          label: METRICS_CONFIG[key].label,
          color: METRICS_CONFIG[key].color,
          planned: goalValue.toFixed(2),
          actual: Math.round(actualValue || 0),
        }
      })
    },
    async loadStatistics() {
      if (!this.studios.length || !this.datePickerRange.length) return
      this.finishedLoading = false
      try {
        const response = await api.fetchTrainerStatistics({
          studios: this.studios.map((s) => s._id),
          range: this.datePickerRange,
        })
        this.statistics = response.statistics
        this.allEmployees = response.employees
      } catch (error) {
        console.error("Fehler beim Laden der Statistiken:", error)
      } finally {
        this.finishedLoading = true
      }
    },
    totalBarChartOptions(metric) {
      const metricKey = Object.keys(METRICS_CONFIG).find((key) => METRICS_CONFIG[key].label === metric.label)
      const color = METRICS_CONFIG[metricKey]?.color || "#00963f"
      return {
        chart: { type: "bar", foreColor: "#fff", toolbar: { show: false } },
        xaxis: { categories: [metric.label], labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" }, formatter: (val) => Math.round(val) } },
        plotOptions: { bar: { horizontal: true } },
        tooltip: { theme: "dark", y: { formatter: (val) => Math.round(val) } },
        colors: ["rgba(0, 150, 63, 0.85)", "rgba(36, 131, 175, 0.85)"], // IST, GEPLANT
        grid: { borderColor: "rgba(255, 255, 255, 0.1)" },
      }
    },
    totalBarChartSeries(metric) {
      return [
        { name: "IST", data: [Math.round(metric.actual)] },
        { name: "Geplant", data: [Math.round(metric.target)] },
      ]
    },
    addColoredDotsToLabels(chartId) {
      setTimeout(() => {
        if (!chartId) return
        const chartEl = document.querySelector(`#${chartId}`)
        if (!chartEl) return
        const labels = chartEl.querySelectorAll(".apexcharts-xaxis-label")
        labels.forEach((label) => {
          if (label.querySelector(".label-dot")) return
          const metricName = label.textContent
          const metric = Object.values(METRICS_CONFIG).find((m) => m.label === metricName)
          if (metric) {
            const dot = document.createElement("span")
            dot.className = "label-dot"
            dot.style.backgroundColor = metric.color
            dot.style.width = "10px"
            dot.style.height = "10px"
            dot.style.borderRadius = "50%"
            dot.style.marginRight = "8px"
            dot.style.display = "inline-block"
            label.prepend(dot)
          }
        })
      }, 100)
    },
    openEmployeeDialog(group) {
      this.selectedDialogGroup = group === "total" ? { name: "Gesamt (alle Studios)", id: "total" } : group
      this.dialogVisible = true
    },
    employeeRadarSeries(employee) {
      if (!employee || !employee.strengths) {
        return []
      }
      const strengthData = METRIC_KEYS.map((key) => employee.strengths[key] || 0)
      return strengthData.map((value, index) => ({
        name: METRICS_CONFIG[METRIC_KEYS[index]].label,
        data: Array(strengthData.length)
          .fill(null)
          .map((_, i) => (i === index ? value : null)),
      }))
    },
  },
  computed: {
    barChartMetricOptions() {
      if (!this.statistics || !this.statistics.totals) return []
      return Object.keys(this.statistics.totals).map((key) => ({
        key: key,
        label: METRICS_CONFIG[key]?.label || key,
      }))
    },
    sortedStudioData() {
      if (!this.statistics || !this.statistics.byStudio) return []
      const key = this.selectedBarChartMetric
      return Object.values(this.statistics.byStudio).sort((a, b) => (b.metrics[key]?.actual || 0) - (a.metrics[key]?.actual || 0))
    },
    studioBarChartOptions() {
      const metricLabel = METRICS_CONFIG[this.selectedBarChartMetric]?.label || ""
      const categories = this.sortedStudioData.map((s, index) => (this.anonymizeStudios ? `Studio ${index + 1}` : s.name))
      return {
        chart: { type: "bar", foreColor: "#fff", toolbar: { show: false } },
        xaxis: { categories, labels: { style: { colors: "#fff" } } },
        yaxis: { title: { text: metricLabel, style: { color: "#fff" } }, labels: { style: { colors: "#fff" }, formatter: (val) => Math.round(val) } },
        tooltip: { theme: "dark", y: { formatter: (val) => Math.round(val) } },
        plotOptions: { bar: { horizontal: false, columnWidth: "60%" } },
        dataLabels: { enabled: false },
        legend: { position: "top", labels: { colors: "#fff" } },
        colors: ["rgba(36, 131, 175, 0.85)", "rgba(0, 150, 63, 0.85)"], // IST, GEPLANT
        grid: { borderColor: "rgba(255, 255, 255, 0.1)" },
      }
    },
    studioBarChartSeries() {
      if (!this.statistics || !this.statistics.byStudio) return []
      const key = this.selectedBarChartMetric
      const actualData = this.sortedStudioData.map((s) => Math.round(s.metrics[key]?.actual || 0))
      const targetData = this.sortedStudioData.map((s) => Math.round(s.metrics[key]?.target || 0))
      return [
        { name: "Geplant", data: targetData },
        { name: "IST", data: actualData },
      ]
    },
    radarChartOptions() {
      return (chartId) => ({
        chart: {
          id: chartId,
          foreColor: "#fff",
          toolbar: { show: false },
          parentHeightOffset: 0,
          events: {
            updated: () => this.addColoredDotsToLabels(chartId),
            mounted: () => this.addColoredDotsToLabels(chartId),
          },
        },
        colors: METRIC_COLORS,
        xaxis: {
          categories: Object.values(METRICS_CONFIG).map((m) => m.label),
          labels: { style: { colors: "#fff", fontSize: "13px" } },
        },
        yaxis: {
          show: true,
          min: 0,
          max: 140,
          tickAmount: 6,
          labels: {
            style: { colors: "rgba(255, 255, 255, 0.5)" }, // Macht die Zahlen dezent
            formatter: (val) => "", // `${Math.round(val)}%`,
          },
        },
        tooltip: {
          theme: "dark",
          custom: function ({ seriesIndex, w }) {
            const metric = METRICS_CONFIG[METRIC_KEYS[seriesIndex]]
            if (!metric) return ""
            const value = Math.round(w.globals.initialSeries[seriesIndex].data.find((v) => v !== null))
            if (isNaN(value)) return ""
            return `<div class="apexcharts-tooltip-custom">
                            <strong style="color: ${metric.color};">${metric.label}:</strong> ${value}%
                        </div>`
          },
        },
        legend: { show: false },
        markers: { size: 6, strokeWidth: 0 },
        plotOptions: {
          radar: {
            polygons: {
              strokeColors: [
                "rgba(255, 255, 255, 0.2)",
                "rgba(255, 255, 255, 1)",
                "rgba(255, 255, 255, 0.2)",
                "rgba(255, 255, 255, 0.2)",
                "rgba(255, 255, 255, 0.2)",
                "rgba(255, 255, 255, 0.2)",
              ],
              connectorColors: "rgba(255, 255, 255, 0.2)",
            },
          },
        },
      })
    },
    totalRadarSeries() {
      if (!this.statistics || !this.statistics.strengths || !this.statistics.strengths.total) {
        return []
      }
      const totalData = this.statistics.strengths.total.map((v) => Math.round(v))
      return totalData.map((value, index) => ({
        name: METRICS_CONFIG[METRIC_KEYS[index]].label,
        data: Array(totalData.length)
          .fill(null)
          .map((_, i) => (i === index ? value : null)),
      }))
    },
    studioRadarSeries() {
      if (!this.statistics || !this.statistics.strengths || !this.statistics.strengths.studios) {
        return []
      }
      return Object.values(this.statistics.strengths.studios).map((s, studioIndex) => ({
        id: Object.keys(this.statistics.strengths.studios)[studioIndex],
        name: this.anonymizeStudios ? `Studio ${studioIndex + 1}` : s.name,
        series: s.data
          .map((v) => Math.round(v))
          .map((value, index) => ({
            name: METRICS_CONFIG[METRIC_KEYS[index]].label,
            data: Array(s.data.length)
              .fill(null)
              .map((_, i) => (i === index ? value : null)),
          })),
      }))
    },
    // KORREKTUR HIER:
    employeesForDialog() {
      if (!this.selectedDialogGroup || !this.allEmployees.length) return []

      let filteredEmployees = []
      if (this.selectedDialogGroup.id === "total") {
        filteredEmployees = this.allEmployees
      } else {
        filteredEmployees = this.allEmployees.filter(
          (emp) => emp.studio && emp.studio.some((studioId) => studioId.toString() === this.selectedDialogGroup.id)
        )
      }

      // NEUE SORTIER-LOGIK
      return filteredEmployees.sort((a, b) => {
        const valA = a.strengths ? a.strengths[this.employeeSortKey] || 0 : 0
        const valB = b.strengths ? b.strengths[this.employeeSortKey] || 0 : 0

        if (this.employeeSortDesc) {
          return valB - valA // Absteigend
        } else {
          return valA - valB // Aufsteigend
        }
      })
    },
  },
}
</script>

<style>
/* Globale Stile sind hier nötig, um die von ApexCharts generierten SVG-Elemente zu stylen */
.apexcharts-tooltip-custom {
  padding: 10px;
  background: #2d2d2d;
  border: 1px solid #555;
  border-radius: 4px;
  color: #fff;
}
</style>

<style scoped>
.card-raised {
  transition: all 0.2s ease-in-out;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.card-raised:hover {
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.4);
}
</style>
